<template>
  <div>
    <DateTimeModal v-if="isDateTimeModalShowing"/>
    <div v-show="canPreviewPlayer">
      <div id="player"></div>
      <ByteArkPlayerContainer
        v-if="!sourceError"
        ref="videoPlayer"
        @ready="onReady"
        @firstplay="onFirstPlay"
        @timeupdate="onTimeUpdate"
        @seeking="onSeeked"
        :options="playerOptions" />
    </div>
    <div
      class="loading"
      v-show="loading">
      <md-progress-spinner class="md-accent" md-mode="indeterminate" />
      <h3 class="loading-text">Loading...</h3>
    </div>
    <div
      :class="{'transparent-container': options.transparentContainer}"
      class="embed-responsive embed-responsive-16by9 error-container"
      v-show="!loading && !canPreviewPlayer">
      <div class="error-help" v-if="!loading && sourceError && !statusMessage.error">
        <small>{{$t('response:video.sourceError')}}</small>
        <div class="code">Code: Network Error</div>
      </div>
      <div class="error-help" v-if="!loading && !sourceError && !statusMessage.error">
        {{$t('response:video.dateTimeUnavailable.title')}}
      </div>
      <div class="error-help" v-if="isShowGeneralError()">
        <small>{{$t('response:video.generalError.title')}}</small>
        <div>{{$t('response:video.generalError.helpText')}}</div>
      </div>
      <div class="error-help" v-if="isShowPreviousProgramError()">
        <small>{{$t('response:video.dateTimeUnavailable.title')}}</small>
        <div>{{$t('response:video.generalError.helpText')}}</div>
      </div>
      <div class="error-help" v-if="isShowLiveError()">
        <small>{{$t('response:video.liveError.title')}}</small>
        <div>{{$t('response:video.liveError.helpText')}}</div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import { mapState, mapActions } from 'vuex';
import ByteArkPlayerContainer from '@byteark/byteark-player-vue';
import DateTimeModal from '@/components/parts/clip/DateTimeModal.vue';

export default {
  name: 'VideoPlayer',
  props: {
    options: {
      type: Object,
    },
    playerEvent: {
      type: Function,
    },
    statusMessage: {
      type: Object,
    },
  },
  components: {
    DateTimeModal,
    ByteArkPlayerContainer,
  },
  data() {
    return {
      player: null,
      fragments: [],
      deadFragments: [],
      data: {},
      isTimeUpdateEmitted: false,
      playerOptions: {
        autoplay: true,
        fluid: true,
        aspectRatio: '16:9',
        liveui: true,
      },
      sourceError: null,
      loading: true,
    };
  },
  computed: {
    ...mapState({
      url: state => state.stream.url,
      seekRequestVideoTime: state => state.stream.seekRequestVideoTime,
      playingRequest: state => state.stream.playingRequest,
      isDateTimeModalShowing: state => state.timeline.isDateTimeModalShowing,
    }),
    canPreviewPlayer() {
      return !!this.player && !!this.player.currentSrc() && !this.statusMessage.error && !this.sourceError;
    },
    deadFragmentsDuration() {
      return _.reduce(this.deadFragments, (sum, fragment) => sum + fragment.duration, 0);
    },
    previewErrorCode() {
      return _.get(this.statusMessage, 'error.status', null);
    },
    previewErrorType() {
      return _.get(this.statusMessage, 'type', null);
    },
  },
  watch: {
    url() {
      this.tryLoadCurrentUrl();
    },
    seekRequestVideoTime(videoTime) {
      if (!this.player) {
        return;
      }
      if (videoTime) {
        this.player.currentTime(videoTime / 1000);
        this.$store.commit('seekRequestComplete');
      }
    },
    playingRequest(value) {
      if (!this.player) {
        return;
      }
      if (value === true) {
        this.player.play();
      } else if (value === false) {
        this.player.pause();
      }
    },
  },
  created() {
    this.playerOptions = {
      ...this.playerOptions,
      ...this.options,
    };
    // Check if the source provided in options is accessible.
    if (this.options.sources && this.options.sources.src) {
      fetch(this.options.sources.src)
        .then()
        .catch((error) => {
          this.sourceError = error;
        });
    }
  },
  methods: {
    ...mapActions([
      'showDateTimeModal',
    ]),
    onReady(newPlayerInstance) {
      this.$emit('ready', newPlayerInstance);
      this.player = newPlayerInstance;
      this.loading = false;

      this.player.on('loadstart', () => {
        this.$store.commit('resetStream');

        this.player.hlsjs.on('hlsLevelLoaded', async (e, data) => {
          this.$store.commit('updateDurationAndTimelinesFromFragments', { data });
          await this.hlsLevelLoadedListener(data.details.fragments);
          await this.durationChangeListener(data.details.totalduration);
        });
      });

      this.player.on('play', () => this.playedListener());
      this.player.on('timeupdate', () => this.timeUpdateListener());
      this.player.on('pause', () => this.pausedListener());
      this.player.on('suspend', () => this.pausedListener());
      this.player.on('abort', () => this.pausedListener());
      this.player.on('error', () => this.pausedListener());
      this.player.on('stalled', () => this.pausedListener());

      this.tryLoadCurrentUrl();
    },
    tryLoadCurrentUrl() {
      if (this.options.sources && this.options.sources.src) {
        this.player.src({
          src: this.options.sources.src,
          type: this.options.sources.type || 'application/x-mpegURL',
        });
      } else if (this.player && this.url) {
        this.player.src({
          src: this.url,
          type: 'application/x-mpegURL',
        });
      }
    },
    playedListener() {
      this.$store.commit('setIsPlaying', true);
    },
    pausedListener() {
      this.$store.commit('setIsPlaying', false);
    },
    timeUpdateListener() {
      const event = {
        type: 'timeupdate',
        payload: this.player.currentTime(),
      };
      this.$store.commit('setPlayheadByVideoTime', this.player.currentTime() * 1000);
      this.$emit('playerEvent', event);
    },
    durationChangeListener(duration) {
      const event = {
        type: 'durationchange',
        payload: {
          duration,
        },
      };
      this.$emit('playerEvent', event);
    },
    async hlsLevelLoadedListener(fragments) {
      this.fragments = fragments;
      const event = {
        type: 'hlslevelloaded',
        payload: {
          fragments,
          deadFragments: this.deadFragments,
        },
      };
      await this.$emit('playerEvent', event);
    },
    getThumbnail() {
      const playerElement = this.$refs.videoPlayer.$el;
      const video = document.querySelector('video');
      const canvas = document.createElement('canvas');
      canvas.width = playerElement.clientWidth;
      canvas.height = playerElement.clientHeight;
      canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
      // TODO: Remove canvas after exporting
      return canvas.toDataURL('png');
    },
    isShowLiveError() {
      return this.previewErrorCode === 400 && this.previewErrorType === 'live';
    },
    isShowPreviousProgramError() {
      return this.previewErrorCode === 400 && this.previewErrorType === 'previousProgram';
    },
    isShowGeneralError() {
      return this.previewErrorCode && this.previewErrorCode !== 400;
    },
    onSeeked(player, currentTime) {
      this.$emit('seeked', currentTime);
    },
    onFirstPlay(player) {
      this.$emit('firstplay', player);
    },
    onTimeUpdate(currentTime) {
      if (!this.isTimeUpdateEmitted) {
        this.$emit('timeupdate', currentTime);
        this.isTimeUpdateEmitted = true;
      }
    },
  },
  beforeDestroy() {
    if (this.player) {
      this.player.dispose();
    }
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/scss/themes/helper.scss';
@import '~@/assets/scss/variables.scss';

.thumbnail {
  display: none;
}
.loading {
  width: 100%;
  height: 100%;
  display: flex;
  flex-flow: column;
  align-items: center;
  justify-content: center;
  .loading-text {
    margin: $spacer 0 0;
  }
}
.error-container {
  @extend .shadow;
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;

  width: 100%;
  height: 100%;
  background-color: $dark-secondary;

  .error-help {
    font-size: $font-size-lg;
    text-align: center;
  }
  .code {
    font-size: $font-size-base * 0.8;
  }

  &.transparent-container {
    background-color: transparent;
    box-shadow: none;
  }
}

</style>
